import React from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  FileText, 
  Send, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Ban,
  HelpCircle 
} from 'lucide-react';
import { cn } from '@/lib/utils';

type EstimateStatus = 'draft' | 'sent' | 'approved' | 'rejected' | 'expired' | 'cancelled';

interface EstimateStatusBadgeProps {
  status: EstimateStatus;
  expiresAt?: string | null;
  showTooltip?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

interface StatusConfig {
  label: string;
  icon: React.ElementType;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  className: string;
  description: string;
  nextSteps: string;
}

const STATUS_CONFIG: Record<EstimateStatus, StatusConfig> = {
  draft: {
    label: 'Draft',
    icon: FileText,
    variant: 'secondary',
    className: 'bg-muted text-muted-foreground border-muted-foreground/20',
    description: 'This estimate is still being worked on and has not been sent to the customer.',
    nextSteps: 'Add items and finalize to send to customer.',
  },
  sent: {
    label: 'Sent',
    icon: Send,
    variant: 'default',
    className: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300',
    description: 'This estimate has been sent to the customer and is awaiting their response.',
    nextSteps: 'Wait for customer approval or follow up with them.',
  },
  approved: {
    label: 'Approved',
    icon: CheckCircle,
    variant: 'default',
    className: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300',
    description: 'The customer has approved this estimate. A project may be created and invoices can be issued.',
    nextSteps: 'Create invoices or make change orders if needed.',
  },
  rejected: {
    label: 'Rejected',
    icon: XCircle,
    variant: 'destructive',
    className: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300',
    description: 'The customer has declined this estimate.',
    nextSteps: 'Create a revised version or archive this estimate.',
  },
  expired: {
    label: 'Expired',
    icon: Clock,
    variant: 'outline',
    className: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300',
    description: 'This estimate has passed its validity period and is no longer active.',
    nextSteps: 'Create a new version with updated pricing and validity.',
  },
  cancelled: {
    label: 'Cancelled',
    icon: Ban,
    variant: 'outline',
    className: 'bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-800 dark:text-gray-400',
    description: 'This estimate has been cancelled and cannot be modified.',
    nextSteps: 'Create a new estimate if needed.',
  },
};

const getEffectiveStatus = (status: EstimateStatus, expiresAt?: string | null): EstimateStatus => {
  if (status === 'sent' && expiresAt) {
    const expiryDate = new Date(expiresAt);
    if (expiryDate < new Date()) {
      return 'expired';
    }
  }
  return status;
};

const EstimateStatusBadge: React.FC<EstimateStatusBadgeProps> = ({
  status,
  expiresAt,
  showTooltip = true,
  size = 'md',
  className,
}) => {
  const effectiveStatus = getEffectiveStatus(status, expiresAt);
  const config = STATUS_CONFIG[effectiveStatus];
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5 gap-1',
    md: 'text-sm px-2.5 py-0.5 gap-1.5',
    lg: 'text-sm px-3 py-1 gap-2',
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-3.5 w-3.5',
    lg: 'h-4 w-4',
  };

  const badge = (
    <Badge
      variant="outline"
      className={cn(
        'inline-flex items-center font-medium border',
        config.className,
        sizeClasses[size],
        className
      )}
    >
      <Icon className={iconSizes[size]} />
      <span>{config.label}</span>
      {showTooltip && (
        <HelpCircle className={cn(iconSizes[size], 'opacity-50')} />
      )}
    </Badge>
  );

  if (!showTooltip) {
    return badge;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent 
          side="bottom" 
          align="start"
          className="max-w-xs p-3"
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4" />
              <span className="font-semibold">{config.label}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {config.description}
            </p>
            <div className="pt-1 border-t">
              <p className="text-xs">
                <span className="font-medium">Next: </span>
                {config.nextSteps}
              </p>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default EstimateStatusBadge;
